import { motion } from "framer-motion";
import { Users, Compass, Handshake, Package, Megaphone, Sparkles } from "lucide-react";

const BOARD = [
  { name: "Dany", role: "Président", initials: "DM", c: "bg-sky-500" },
  { name: "Julien", role: "Vice-Président", initials: "JL", c: "bg-amber-400" },
  { name: "Marie", role: "Trésorière", initials: "MD", c: "bg-rose-400" },
];

const ANIMATORS = [
  { name: "Sarah", role: "Animatrice numérique", initials: "SL", c: "bg-sky-400" },
  { name: "Yanis", role: "Animateur robotique", initials: "YM", c: "bg-amber-500" },
  { name: "Chloé", role: "Animatrice créative", initials: "CD", c: "bg-emerald-400" },
];

const SERVICES = [
  {
    icon: Compass,
    c: "bg-sky-100 text-sky-600",
    title: "Direction",
    text: "Assure la vision stratégique, la coordination générale et le développement de l'association. Veille à l'alignement des activités avec les objectifs pédagogiques et les référentiels en vigueur (FMTTN).",
  },
  {
    icon: Handshake,
    c: "bg-amber-100 text-amber-600",
    title: "Service Clients et relation partenaires",
    text: "Gère les demandes de réservation, l'accueil des écoles et des familles, et entretient les relations avec les partenaires institutionnels, communaux et culturels (Fédération Wallonie-Bruxelles, villes, musées, etc.).",
  },
  {
    icon: Package,
    c: "bg-emerald-100 text-emerald-600",
    title: "Service Achats et logistique",
    text: "Assure l'acquisition et l'entretien du matériel pédagogique (PC, tablettes, supports « hors ligne », badges, gommettes). Garantit la disponibilité des équipements et la mise en œuvre du « Plan B » en cas de panne technique.",
  },
  {
    icon: Megaphone,
    c: "bg-rose-100 text-rose-600",
    title: "Service Marketing et Communication",
    text: "Conçoit les supports de promotion, anime les réseaux sociaux et le site web, et assure la visibilité d'Kwarizmix auprès du grand public, des écoles et des institutions.",
  },
  {
    icon: Sparkles,
    c: "bg-violet-100 text-violet-600",
    title: "Service Animation",
    text: "Composé d'animateurs spécialisés en éducation numérique, il conçoit et anime les ateliers adaptés à chaque niveau scolaire. Chaque enfant dispose de son propre matériel pour relever des défis ludiques, expérimenter et progresser à son propre rythme.",
  },
];

function PersonCard({ p, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: (index % 3) * 0.08 }}
      className="flex items-center gap-4 bg-white rounded-3xl border border-slate-100 shadow-sm p-6"
      data-testid={`team-${p.initials}`}
    >
      <span className={`grid place-items-center w-16 h-16 rounded-2xl text-white font-heading font-black text-xl ${p.c}`}>
        {p.initials}
      </span>
      <div>
        <p className="font-heading font-extrabold text-lg text-slate-900">{p.name}</p>
        <p className="text-sm text-slate-500">{p.role}</p>
      </div>
    </motion.div>
  );
}

export default function Equipe() {
  return (
    <div data-testid="equipe-page">
      <section className="bg-gradient-to-b from-amber-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600">L'Académie · Équipe</span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-slate-900 mt-2">
            L'équipe Kwarizmix
          </h1>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl">
            Des passionnés du numérique et de la pédagogie, au service de l'éveil des enfants.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-6">
        <p className="text-lg text-slate-700 leading-relaxed" data-testid="equipe-intro">
          L'équipe d'Kwarizmix, des animateurs passionnés et engagés qui réalisent des ateliers
          numériques pour enfants tout au long de l'année dans le cadre d'animations en école primaire
          (de la 3<sup>e</sup> à la 6<sup>e</sup>).
        </p>
        <p className="text-lg text-slate-700 leading-relaxed">
          Notre organisation reflète la structure d'une ASBL à taille humaine tout en anticipant les
          besoins spécifiques d'Kwarizmix : logistique numérique (matériel, maintenance), relation
          avec les partenaires stratégiques, et pédagogie adaptée aux enjeux de l'IA et du numérique.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <h2 className="font-heading font-extrabold text-3xl text-slate-900 mb-8">Notre organisation</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 3) * 0.08 }}
                className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6"
                data-testid={`service-${i}`}
              >
                <span className={`grid place-items-center w-12 h-12 rounded-2xl mb-4 ${s.c}`}>
                  <Icon className="w-6 h-6" />
                </span>
                <p className="font-heading font-extrabold text-lg text-slate-900 mb-2">{s.title}</p>
                <p className="text-sm text-slate-600 leading-relaxed">{s.text}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-3 mb-8">
          <span className="grid place-items-center w-12 h-12 rounded-2xl bg-sky-100 text-sky-600"><Users className="w-6 h-6" /></span>
          <h2 className="font-heading font-extrabold text-3xl text-slate-900">Nos animateurs</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...BOARD, ...ANIMATORS].map((p, i) => <PersonCard key={p.name} p={p} index={i} />)}
        </div>
      </section>
    </div>
  );
}
